import React, { useState } from "react";
import { ApiBookmark } from "../lib/api-client";
import BookmarkRow from "./BookmarkRow";

interface Props {
  bookmarks: ApiBookmark[];
  onSeek: (seconds: number) => void;
  onAdd: (label: string) => Promise<void>;
  onEdit: (
    id: string,
    patch: { label?: string; timestampSec?: number },
  ) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export default function BookmarksSection({
  bookmarks,
  onSeek,
  onAdd,
  onEdit,
  onDelete,
}: Props) {
  const [draftLabel, setDraftLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setAddError(null);
    setAdding(true);
    try {
      await onAdd(draftLabel.trim());
      setDraftLabel("");
    } catch (err) {
      setAddError(err instanceof Error ? err.message : String(err));
    } finally {
      setAdding(false);
    }
  }

  return (
    <section className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-[10px] tracking-[0.28em] uppercase text-gray-500">
          Bookmarks
        </h2>
        <span className="text-xs text-gray-600 tabular-nums">
          {bookmarks.length}
        </span>
      </div>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={draftLabel}
          onChange={(e) => setDraftLabel(e.target.value)}
          maxLength={120}
          placeholder="What to remember about this moment…"
          className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-orange-500"
        />
        <button
          type="submit"
          disabled={adding}
          className="px-3 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white text-sm rounded transition"
        >
          {adding ? "Saving…" : "Bookmark this moment"}
        </button>
      </form>
      {addError && <p className="text-xs text-red-400">{addError}</p>}

      {bookmarks.length === 0 ? (
        <p className="font-display-italic text-xs text-gray-600">
          Nothing bookmarked yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {bookmarks.map((b) => (
            <BookmarkRow
              key={b.id}
              bookmark={b}
              onSeek={onSeek}
              onEdit={(patch) => onEdit(b.id, patch)}
              onDelete={() => onDelete(b.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
